import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import { Button, cn, useI18n } from '@xdocs/ui';
import type { AutosaveStatus } from './useEditorAutosave.js';
import { useEditorT } from './useEditorT.js';
import styles from './AutosaveIndicator.module.css';

export interface AutosaveIndicatorProps {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  onRetry?: () => void;
  className?: string | undefined;
}

const SAVED_JUST_NOW_WINDOW_MS = 60_000;
const RECENT_TONE: Record<AutosaveStatus, 'saved' | 'saving' | 'pending' | 'error' | 'neutral'> = {
  idle: 'neutral',
  dirty: 'neutral',
  saving: 'saving',
  saved: 'saved',
  'offline-pending': 'pending',
  error: 'error',
  conflict: 'error',
};

/** `Date.now()` is impure and may not be called during render (react-hooks
 * purity rule) — this tracks "is `lastSavedAt` still within the 'just now'
 * window" as real state, computed/refreshed only inside an effect (on
 * mount, on `lastSavedAt` change, and via a timer once the window elapses),
 * never read from the wall clock directly in the render body. */
function useIsWithinJustNowWindow(lastSavedAt: Date | null): boolean {
  const [isRecent, setIsRecent] = useState(false);

  // A "relative time ago" display fundamentally can't satisfy BOTH "no
  // impure calls during render" (`Date.now()`) AND "never setState inside
  // an effect" at once — the value depends on the wall clock, which can
  // only be read outside render. This effect is the least-impure resolution
  // (synchronize once on `lastSavedAt` change + a self-clearing timer),
  // reviewed and intentional.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!lastSavedAt) {
      setIsRecent(false);
      return undefined;
    }
    const elapsed = Date.now() - lastSavedAt.getTime();
    if (elapsed >= SAVED_JUST_NOW_WINDOW_MS) {
      setIsRecent(false);
      return undefined;
    }
    setIsRecent(true);
    const timer = setTimeout(() => setIsRecent(false), SAVED_JUST_NOW_WINDOW_MS - elapsed);
    return () => clearTimeout(timer);
  }, [lastSavedAt]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return isRecent;
}

/**
 * F67 §28.1 autosave/version indicator ("Saving…" / "Saved · just now" /
 * "All changes saved" / save-error + retry) — the E19 in-flight/outcome
 * state for the one continuous background mutation the editor runs. The
 * `conflict` status renders nothing here — `ConflictBanner` is the
 * authoritative, blocking surface for that state.
 */
export function AutosaveIndicator({ status, lastSavedAt, onRetry, className }: AutosaveIndicatorProps): ReactElement | null {
  const { t } = useEditorT();
  const { locale } = useI18n();
  const isRecent = useIsWithinJustNowWindow(lastSavedAt);

  if (status === 'conflict') return null;

  const tone = RECENT_TONE[status];
  let label: string;
  if (status === 'idle') label = t('editor:autosave.idle');
  else if (status === 'dirty') label = t('editor:autosave.unsaved');
  else if (status === 'saving') label = t('editor:autosave.saving');
  else if (status === 'offline-pending') label = t('editor:autosave.offlinePending');
  else if (status === 'error') label = t('editor:autosave.error');
  else {
    label = isRecent
      ? t('editor:autosave.saved')
      : t('editor:autosave.savedAt', { time: lastSavedAt ? formatDate(locale, lastSavedAt, { hour: '2-digit', minute: '2-digit' }) : '' });
  }

  return (
    <span className={cn(styles['indicator'], className)} aria-live="polite">
      <span className={styles['dot']} data-tone={tone} aria-hidden="true" />
      <span className={styles['label']} data-tone={tone}>
        {label}
      </span>
      {status === 'error' && onRetry && (
        <Button variant="ghost" size="sm" className={styles['retry']} onClick={onRetry}>
          {t('editor:autosave.retry')}
        </Button>
      )}
    </span>
  );
}
